/**
 * Series shaping for the metrics chart: grouping, windowing and smoothing.
 *
 * Kept free of Chart.js and the DOM so the numeric behaviour is unit-testable;
 * `metrics-chart.ts` only renders what this module returns. Smoothing applies to
 * both series, so the loss curve never looks like it was produced by different
 * rules than the metric curve.
 */
import type { MetricPoint } from './snapshots.ts';

export type XAxisMode = 'batch' | 'epoch';
export type MetricName = 'accuracy' | 'precision' | 'recall' | 'f1';
export type DisplayMode = 'raw' | 'moving';

export const METRIC_LABELS: Record<MetricName, string> = {
  accuracy: 'Accuracy',
  precision: 'Precision',
  recall: 'Recall',
  f1: 'F1',
};

/** One sentence per metric: the audience is not assumed to know these terms. */
export const METRIC_HINTS: Record<MetricName, string> = {
  accuracy: 'Share of all digit guesses that were correct.',
  precision: 'Of the digits it claimed to be a class, how many really were.',
  recall: 'Of the digits that really were a class, how many it found.',
  f1: 'One balanced score combining precision and recall.',
};

/**
 * Smoothing window in x-axis units. Batches move fast enough to warrant a wider
 * window; epochs are already an aggregate, so a long window would only flatten
 * them into a cumulative mean.
 */
const SMOOTHING_WINDOW: Record<XAxisMode, number> = { batch: 10, epoch: 5 };

export interface SeriesPoint {
  /** X value: the batch step or the 1-based epoch number. */
  label: number;
  loss: number;
  value: number;
}

export interface SeriesOptions {
  xAxis: XAxisMode;
  metric: MetricName;
  display: DisplayMode;
  /** Keep only the last N x-axis units; `null` keeps the whole history. */
  span: number | null;
}

/**
 * Turns the raw per-batch history into the two plotted series.
 *
 * Smoothing runs over the whole history before the window is applied, so the
 * first visible point is not averaged over a truncated window.
 */
export function buildSeries(points: readonly MetricPoint[], options: SeriesOptions): SeriesPoint[] {
  const units =
    options.xAxis === 'epoch'
      ? groupByEpoch(points, options.metric)
      : perBatch(points, options.metric);
  const smoothed = smooth(units, options.display, SMOOTHING_WINDOW[options.xAxis]);

  return options.span === null ? smoothed : smoothed.slice(-options.span);
}

function perBatch(points: readonly MetricPoint[], metric: MetricName): SeriesPoint[] {
  return points.map((point) => ({ label: point.step, loss: point.loss, value: point[metric] }));
}

/**
 * Collapses each epoch into one point by averaging its batches.
 *
 * Averaging the existing batch points keeps a single source of truth and makes
 * the epoch curve comparable with the batch curve. These are training metrics,
 * not the held-out evaluation shown in the metric grid below the chart.
 */
function groupByEpoch(points: readonly MetricPoint[], metric: MetricName): SeriesPoint[] {
  const buckets = new Map<number, { count: number; loss: number; value: number }>();

  for (const point of points) {
    const bucket = buckets.get(point.epoch) ?? { count: 0, loss: 0, value: 0 };
    bucket.count += 1;
    bucket.loss += point.loss;
    bucket.value += point[metric];
    buckets.set(point.epoch, bucket);
  }

  return Array.from(buckets, ([epoch, bucket]) => ({
    label: epoch,
    loss: bucket.loss / bucket.count,
    value: bucket.value / bucket.count,
  }));
}

function smooth(
  units: readonly SeriesPoint[],
  display: DisplayMode,
  window: number,
): SeriesPoint[] {
  if (display === 'raw') {
    return units.map((unit) => ({ ...unit }));
  }

  const smoothedLoss = movingAverage(
    units.map((unit) => unit.loss),
    window,
  );
  const smoothedValue = movingAverage(
    units.map((unit) => unit.value),
    window,
  );

  // `movingAverage` preserves length, so the indices line up with `units`.
  return units.map((unit, index) => ({
    label: unit.label,
    loss: smoothedLoss[index]!,
    value: smoothedValue[index]!,
  }));
}

/** Trailing moving average; the first points average over fewer samples. */
export function movingAverage(values: readonly number[], window: number): number[] {
  const result: number[] = [];
  let sum = 0;

  values.forEach((value, index) => {
    sum += value;
    const expired = values[index - window];
    if (expired !== undefined) {
      sum -= expired;
    }
    result.push(sum / Math.min(index + 1, window));
  });

  return result;
}
